import { useState } from 'react';
import { prefixes, useTypedTranslation } from "@/locales";
import { useNavigate } from 'react-router';
import { Button } from '@/components/_untitled/base/buttons/button';
import { AlertTriangle, ChevronDown, ChevronUp } from '@untitledui/icons';

interface ErrorFallbackProps {
  error: Error | null;
  onReset?: () => void;
}

export const ErrorFallback = ({ error, onReset }: ErrorFallbackProps) => {
  const [tCommon, tErrors] = useTypedTranslation(prefixes('common', 'errors'));
  const navigate = useNavigate();
  const [showDetails, setShowDetails] = useState(false);

  const isDevelopment = import.meta.env.DEV;

  return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="max-w-md w-full p-8 bg-white border border-border-secondary rounded-lg">
          <div className="flex justify-center mb-4">
            <div className="size-16 rounded-full bg-warning-50 flex items-center justify-center">
              <AlertTriangle className="size-8 text-warning-600"/>
            </div>
          </div>

          <h1 className="text-2xl font-semibold text-fg-primary mb-2 text-center">
            { tErrors('somethingWentWrong') }
          </h1>

          <p className="text-fg-secondary mb-6 text-center">
            { tErrors('unexpectedError') }
          </p>

          <div className="flex gap-3 justify-center mb-4">
            { onReset && (
                <Button color="primary" onClick={ onReset }>
                  { tErrors('tryAgain') }
                </Button>
            ) }
            <Button color="secondary" onClick={ () => navigate('/') }>
              { tCommon('goToHome') }
            </Button>
          </div>

          { isDevelopment && error && (
              <div className="mt-4 border-t border-border-secondary pt-4">
                <button
                    onClick={ () => setShowDetails(!showDetails) }
                    aria-expanded={ showDetails }
                    aria-controls="error-details"
                    className="flex items-center gap-2 text-xs text-fg-quinary hover:text-fg-secondary w-full"
                >
                  { showDetails ? <ChevronUp className="size-4"/> : <ChevronDown className="size-4"/> }
                  { tErrors('errorDetails') }
                </button>

                { showDetails && (
                    <div
                        id="error-details"
                        className="mt-2 p-3 bg-gray-50 rounded text-xs font-mono text-fg-secondary overflow-auto max-h-48"
                    >
                      <div className="font-semibold mb-1">{ error.name }</div>
                      <div className="mb-2">{ error.message }</div>
                      { error.stack && (
                          <div className="text-[10px] text-fg-quinary whitespace-pre-wrap">
                            { error.stack }
                          </div>
                      ) }
                    </div>
                ) }
              </div>
          ) }
        </div>
      </div>
  );
};
